<template>
  <div class="menuBar_container font-sans" :class="{ expand: menuStore.isExpand }">
    <div class="menuBar_content">
      <div class="menuBar_header">
        <div class="logo_box">
          <svg-icon icon="banner-logo-icon" :size="30"></svg-icon>
        </div>
        <div class="avatar_box">
          <img :src="avatarImage" alt="" width="59" height="59" />
        </div>
      </div>

      <div class="menuList_box">
        <div
          class="menuItem"
          v-for="(menuItem, index) in menuList"
          :key="index"
          data-focus-type="menu"
          :data-focus-index="index"
          tabindex="0"
          :class="{ active: $route.name === menuItem.routeName }"
          @focus="menuItem.handleFocus"
          @click="menuItem.handleClick"
        >
          <svg-icon class="menuItem_focus_icon" :icon="menuItem.focusIcon" :size="24"></svg-icon>

          <svg-icon class="menuItem_icon" :icon="menuItem.icon" :size="24"></svg-icon>

          <div class="menuItemName">{{ menuItem.name }}</div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { ref, watch, onMounted, onBeforeUnmount, computed } from 'vue';
import { getCurrentInstance } from 'vue';
import FocusManager from '../utils/focus-manager';
import useMenuStore from '@/store/menu';
import SvgIcon from '@/components/svg-icon/index.vue';
import focusManager from '../utils/focus-manager';
import { useI18n } from 'vue-i18n';
import { detectPlatform } from '@/utils/device';
import { transformImageUrlWithDimensions } from '@/utils/transformed-image';
import { IMAGE_DIMENSIONS, getDefaultPreset } from '@/config/image-config';
import defaultAvatar from '@/assets/images/default-avatar.png';
import { useFeatureFlags } from '@/utils/useFeatureFlags';
import { focusElementCache } from '@/utils/focus-optimization';
import useUserStore from '@/store/user';

const instance = getCurrentInstance()?.proxy;
const { t } = useI18n();

const menuStore = useMenuStore();
// const userInfo = ref(null);
const userStore = useUserStore();
const userInfo = computed(() => userStore.userInfo);
const { fetchFeatureFlags, isFlagEnabled } = useFeatureFlags();

const moviesSeriesEnabled = computed(() => isFlagEnabled('moviesSeries', false));

const beforFocusEl = ref(null);
let menuCloseTimeout = null;

const avatarImage = computed(() => {
  const avatarUrl = userInfo.value?.avatar;
  if (avatarUrl) {
    return transformImageUrlWithDimensions(
      avatarUrl,
      IMAGE_DIMENSIONS.AVATAR.MENU.width,
      IMAGE_DIMENSIONS.AVATAR.MENU.height,
      'medium',
      'cover',
      {
        preset: getDefaultPreset('AVATAR'),
        format: 'png',
      },
    );
  }
  return defaultAvatar;
});

const focusMenuItemByRoute = routeName => {
  const menuItemsEl = focusElementCache.get('.menuItem');
  const focusIndex = menuList.value.findIndex(item => item.routeName === routeName);
  if (focusIndex >= 0 && menuItemsEl[focusIndex]) {
    focusManager.setFocus(menuItemsEl[focusIndex]);
  }
};

// LG TV specific focus helper
const focusMainContent = () => {
  // Try to focus on the first banner or main content element (cached)
  const bannerEl = focusElementCache.get('[data-focus-type="banner"]')[0];
  const firstCard = focusElementCache.get('[data-focus-type*="matchCardGroup"]')[0];
  const homeContainer = focusElementCache.get('.home_container')[0];

  if (bannerEl) {
    FocusManager.setFocus(bannerEl);
    console.log('########### trying to focus first banner: ', bannerEl);
  } else if (firstCard) {
    FocusManager.setFocus(firstCard);
  } else if (homeContainer) {
    // Last resort: focus on home container and let FocusManager handle it
    FocusManager.init();
  }
};

watch(
  () => menuStore.isExpand,
  newVal => {
    const menuItemsEl = focusElementCache.get('.menuItem');
    const focusMenuItemIndex = menuList.value.findIndex(item => item.routeName === instance.$route.name);
    const focusIndex = focusMenuItemIndex >= 0 ? focusMenuItemIndex : 0;
    console.log('######## menuItemsEl :', menuItemsEl);
    console.log('######## focusMenuItemIndex : ', focusMenuItemIndex);

    if (newVal) {
      // Menu is opening
      // Cancel any pending close timeout
      if (menuCloseTimeout) {
        console.log('[MENU DEBUG] Canceling pending menu close timeout');
        clearTimeout(menuCloseTimeout);
        menuCloseTimeout = null;
      }

      // Store the current focused element BEFORE opening the menu
      const currentFocus = FocusManager.getCurFocusEl();
      if (currentFocus && currentFocus.dataset.focusType !== 'menu') {
        beforFocusEl.value = currentFocus;
        console.log('[MENU DEBUG] Storing focus element before menu open:', beforFocusEl.value);
      }

      menuItemsEl.forEach(item => {
        item.dataset.focusable = '';
      });

      // Invalidate element cache after adding data-focusable attributes
      FocusManager.invalidateCache();

      setTimeout(() => {
        const targetEl = menuItemsEl[focusIndex];
        if (targetEl) {
          FocusManager.setFocus(targetEl);
        }
      }, 10);
    } else {
      // Menu is closing
      // CRITICAL: Clear focus from menu items first to avoid broken state
      const currentFocus = FocusManager.getCurFocusEl();
      if (currentFocus && currentFocus.dataset.focusType === 'menu') {
        console.log('[MENU DEBUG] Clearing focus from menu item before closing');
        FocusManager.clearFocus();
      }

      menuItemsEl.forEach(item => {
        item.removeAttribute('data-focusable');
      });

      // Invalidate element cache after removing data-focusable attributes
      FocusManager.invalidateCache();

      // Don't restore focus if we're on the settings page - let settingPage handle it
      if (instance.$route.path === '/setting') {
        console.log('[MENU DEBUG] On settings page, skipping focus restoration');
        return;
      }

      // Platform-specific timeout and handling
      const platform = detectPlatform();
      const timeout = platform === 'webos' ? 50 : 10; // LG TV needs more time

      setTimeout(() => {
        console.log('[MENU DEBUG] Menu closing, attempting focus restoration');
        console.log('[MENU DEBUG] beforFocusEl.value:', beforFocusEl.value);
        console.log('[MENU DEBUG] beforFocusEl.value visible?', beforFocusEl.value?.offsetParent !== null);

        // CRITICAL: Check if FocusManager has already restored focus (e.g., from route navigation)
        // If there's already a focused element that's not a menu item and is visible, don't override it
        const currentFocus = FocusManager.getCurFocusEl();
        const hasValidFocus =
          currentFocus && currentFocus.dataset.focusType !== 'menu' && currentFocus.offsetParent !== null;

        if (hasValidFocus) {
          console.log(
            '[MENU DEBUG] FocusManager has already restored focus to:',
            currentFocus.dataset.focusIndex,
            '- not overriding',
          );
          // Don't restore focus - FocusManager has already handled it
          return;
        }

        // Prioritize restoring to the previously focused element
        if (beforFocusEl.value && beforFocusEl.value.offsetParent !== null) {
          console.log(
            '[MENU DEBUG] Restoring focus to previously focused element:',
            beforFocusEl.value.dataset.focusIndex,
          );
          FocusManager.setFocus(beforFocusEl.value);
        } else {
          // Fallback to banner or first card only if no previous focus was saved
          const bannerEl = focusElementCache.get('[data-focus-type="banner"]')[0];
          const firstCard = focusElementCache.get('[data-focus-type*="matchCardGroup"]')[0];

          console.log('[MENU DEBUG] Banner element found:', bannerEl);
          console.log('[MENU DEBUG] First card found:', firstCard);

          if (bannerEl && bannerEl.offsetParent !== null) {
            console.log('[MENU DEBUG] Setting focus to banner');
            FocusManager.setFocus(bannerEl);
          } else if (firstCard && firstCard.offsetParent !== null) {
            console.log('[MENU DEBUG] Setting focus to first card');
            FocusManager.setFocus(firstCard);
          } else {
            console.log('[MENU DEBUG] No suitable element found, calling focusMainContent');
            focusMainContent();
          }
        }

        // Verify focus was set
        setTimeout(() => {
          const currentFocus = FocusManager.getCurFocusEl();
          console.log('[MENU DEBUG] Current focus after restoration:', currentFocus);
          console.log('[MENU DEBUG] Focus type:', currentFocus?.dataset?.focusType);
        }, 100);
      }, timeout);
    }
  },
);

watch(
  () => instance.$route,
  newVal => {
    const curFocusEl = FocusManager.getCurFocusEl();
    const menuItemsEl = focusElementCache.get('.menuItem');
    beforFocusEl.value = null;
    if (newVal.name === 'Home' && curFocusEl?.dataset?.focusIndex === '1') {
      FocusManager.setFocus(menuItemsEl[0]);
    }
  },
);

const menuList = computed(() => {
  const items = [
    {
      name: t('search'),
      routeName: 'Search',
      icon: 'banner-search-icon',
      focusIcon: 'banner-search-focus-icon',
      handleFocus: () => {
        // Just handle focus styling - don't navigate on focus
        // Navigation should only happen on click/enter
      },
      handleClick: () => {
        if (!menuStore.isExpand) {
          return;
        }
        focusMenuItemByRoute('Search');
        if (instance.$route.path !== '/search') {
          beforFocusEl.value = null;
          // Pass the current route path so we can return to it after language change
          instance.$router.push({
            path: '/search',
          });
          // Close menu after settings page is mounted and ready
          menuCloseTimeout = setTimeout(() => {
            menuStore.setMenuExpand(false);
            menuCloseTimeout = null;
          }, 150);
        }
      },
    },
    {
      name: t('home'),
      routeName: 'Home',
      icon: 'banner-home-icon',
      focusIcon: 'banner-home-focus-icon',
      handleFocus: () => {
        // Just handle focus styling - don't navigate on focus
        // Navigation should only happen on click/enter
      },
      handleClick: () => {
        if (!menuStore.isExpand) {
          return;
        }

        beforFocusEl.value = null;

        // Always close menu
        menuStore.setMenuExpand(false);

        // If not on home, navigate
        if (instance.$route.path !== '/') {
          instance.$router.push('/');
        }
      },
    },
    {
      name: t('sport'),
      routeName: 'Sports',
      icon: 'banner-sports-icon',
      focusIcon: 'banner-sports-focus-icon',
      handleFocus: () => {},
      handleClick: () => {
        if (!menuStore.isExpand) {
          return;
        }

        beforFocusEl.value = null;
        menuStore.setMenuExpand(false);

        if (instance.$route.path !== '/sports') {
          instance.$router.push('/sports');
        }
      },
    },
    {
      name: t('shows'),
      routeName: 'Shows',
      icon: 'banner-shows-icon',
      focusIcon: 'banner-shows-focus-icon',
      handleFocus: () => {},
      handleClick: () => {
        if (!menuStore.isExpand) {
          return;
        }

        beforFocusEl.value = null;
        menuStore.setMenuExpand(false);

        if (instance.$route.path !== '/shows') {
          instance.$router.push('/shows');
        }
      },
    },
  ];

  if (moviesSeriesEnabled.value) {
    items.push({
      name: t('movies-and-series'),
      routeName: 'SeriesAndMovies',
      icon: 'banner-movies-icon',
      focusIcon: 'banner-movies-focus-icon',
      handleFocus: () => {},
      handleClick: () => {
        if (!menuStore.isExpand) {
          return;
        }

        beforFocusEl.value = null;
        menuStore.setMenuExpand(false);

        if (instance.$route.path !== '/series-and-movies') {
          instance.$router.push('/series-and-movies');
        }
      },
    });
  }

  items.push({
    name: t('setting'),
    routeName: 'Setting',
    icon: 'banner-setting-icon',
    focusIcon: 'banner-setting-focus-icon',
    handleFocus: () => {
      // Just handle focus styling - don't navigate on focus
      // Navigation should only happen on click/enter
    },
    handleClick: () => {
      if (!menuStore.isExpand) {
        return;
      }
      focusMenuItemByRoute('Setting');
      if (instance.$route.path !== '/setting') {
        beforFocusEl.value = null;
        // Pass the current route path so we can return to it after language change
        instance.$router.push({
          path: '/setting',
          query: { from: instance.$route.path },
        });
        // Close menu after settings page is mounted and ready
        menuCloseTimeout = setTimeout(() => {
          menuStore.setMenuExpand(false);
          menuCloseTimeout = null;
        }, 150);
      }
    },
  });

  return items;
});

onMounted(() => {
  // Remove movement restrictions for menu to allow free navigation
  // FocusManager.setMoveAreaLimit('menu', ['up', 'down']);
  fetchFeatureFlags();
  // userInfo.value = getStorage(STORAGE_KEYS.USER_INFO);
});

onBeforeUnmount(() => {
  FocusManager.removeMoveAreaLimit('menu');
});
</script>
<style lang="less" scoped>
.menuBar_container {
  position: fixed;
  top: 0;
  left: 0;
  width: 80px;
  height: 100%;
  background: -webkit-linear-gradient(left, rgba(26, 26, 26, 0.9) 0%, rgba(26, 26, 26, 0) 45%);
  background: -moz-linear-gradient(left, rgba(26, 26, 26, 0.9) 0%, rgba(26, 26, 26, 0) 45%);
  background: -o-linear-gradient(left, rgba(26, 26, 26, 0.9) 0%, rgba(26, 26, 26, 0) 45%);
  background: -ms-linear-gradient(left, rgba(26, 26, 26, 0.9) 0%, rgba(26, 26, 26, 0) 45%);
  background: linear-gradient(90deg, rgba(26, 26, 26, 0.9) 0%, transparent 45%);
  z-index: 20;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 24px 0;
  box-sizing: border-box;
  transition: width 0.3s ease;

  .menuBar_content {
    display: flex;
    flex-direction: column;
    align-items: center;
    align-self: stretch;
    flex: 1;
    width: 73px;
    transition: width 0.3s ease;
  }

  .menuBar_header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    z-index: 1;

    .avatar_box {
      display: none;
    }
  }

  .menuList_box {
    z-index: 1;
    display: flex;
    flex-direction: column;
    margin: auto 0;

    // gap: 24px;
    .menuItem {
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 12px;
      border-radius: 50px;
      color: rgba(255, 255, 255, 0.75);
      width: 56px;
      height: 40px;

      &:last-child {
        margin-bottom: 0;
      }

      .menuItem_icon {
        display: block;
      }

      .menuItem_focus_icon {
        display: none;
      }

      .menuItemName {
        display: none;
        font-weight: 500;
        font-size: 12px;
        color: rgba(255, 255, 255, 0.75);
        font-feature-settings: 'ss01' on;
        margin-inline-start: 12px;
      }

      &.focused {
        width: 200px;
        background: #ffffff;
        border-radius: 50px;
        color: #ffffff;

        .menuItem_icon {
          display: none;
        }

        .menuItem_focus_icon {
          display: block;
        }
      }

      &.active {
        background: rgba(255, 255, 255, 0.2);

        .menuItem_icon {
          display: none;
        }

        .menuItem_focus_icon {
          display: block;
        }
      }
    }
  }

  .avatar_box {
    z-index: 1;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.2);
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      display: block;
    }
  }

  &.expand {
    width: auto;
    padding: 8px;
    background: transparent;

    .menuBar_content {
      width: 224px;
      position: relative;
      padding: 24px 12px;
      border-radius: 32px;
      border: 1px solid;
      background: rgba(26, 26, 26, 0.9);
      background-blend-mode: luminosity;
    }

    .menuBar_header {
      justify-content: space-between;
      width: 100%;
      padding: 0 12px;

      .avatar_box {
        display: block;
      }
    }

    .menuList_box {
      .menuItem {
        justify-content: start;
        width: 200px;
        border-radius: 50px;
        padding: 0 12px;

        &.active {
          background: rgba(255, 255, 255, 0.12);
        }

        &.focused {
          background: #ffffff;
          color: #000000;

          .menuItemName {
            color: #000000;
          }

          .menuItem_focus_icon {
            display: block;
          }

          .menuItem_icon {
            display: none;
          }
        }

        .menuItemName {
          display: block;
          margin-inline-end: auto;
        }
      }
    }
  }
}

html[lang='ar'] {
  .menuBar_container {
    background: -webkit-linear-gradient(right, rgba(26, 26, 26, 0.9) 0%, rgba(26, 26, 26, 0) 45%);
    background: -moz-linear-gradient(right, rgba(26, 26, 26, 0.9) 0%, rgba(26, 26, 26, 0) 45%);
    background: -o-linear-gradient(right, rgba(26, 26, 26, 0.9) 0%, rgba(26, 26, 26, 0) 45%);
    background: -ms-linear-gradient(right, rgba(26, 26, 26, 0.9) 0%, rgba(26, 26, 26, 0) 45%);
    background: linear-gradient(-90deg, rgba(26, 26, 26, 0.9) 0%, transparent 45%);
    right: 0;
    left: auto;
  }
}
</style>
